'use client';

import * as m from 'motion/react-m';
import React, { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useZendeskNotices } from '@flipster/features/src/shared/zendesk/use-zendesk-notices';
import { FormattedMessage } from '@flipster/i18n';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { Styles, css } from '@flipster/styled-system/css';
import { center, divider, flex, hstack } from '@flipster/styled-system/patterns';

import { maxWStyles } from '@web/app/[locale]/(web)/_main-page/_styles';

const Announcement = ({ css: cssProp }: { css?: Styles }) => {
  const timer = useRef<NodeJS.Timeout | null>(null);

  const [noticeIndex, setNoticeIndex] = useState<number>(0);

  const { isLoggedIn } = useAuth();
  const { notices } = useZendeskNotices();

  const onClickPagination: MouseEventHandler<HTMLButtonElement> = (e) => {
    setNoticeIndex(Number(e.currentTarget.value));
  };

  const noticeLength = notices?.length ?? 0;

  const setNoticeTimer = useCallback(() => {
    if (noticeLength === 1) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setNoticeIndex((prevState) => {
        let nextIndex = prevState + 1;
        if (nextIndex === noticeLength) nextIndex = 0;
        return nextIndex;
      });
    }, 5 * 1000);
  }, [noticeLength]);

  useEffect(() => {
    setNoticeTimer();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [noticeIndex, setNoticeTimer]);

  const notice = notices?.[noticeIndex];

  if (!isLoggedIn) return null;

  return (
    <section
      className={css({
        bgColor: 'background.default.level-1',
        h: { base: 166, tablet: 80 },
        mt: { base: 56, tablet: 100 },
        ...cssProp,
      })}
    >
      <div
        className={css(
          flex.raw({
            h: '100%',
            px: { base: 16, tablet: 24 },
            py: 16,
            flexDirection: { base: 'column', tablet: 'row' },
            alignItems: { base: 'flex-start', tablet: 'center' },
          }),
          maxWStyles,
        )}
      >
        <div
          className={flex({
            flex: 1,
            flexDirection: { base: 'column', tablet: 'row' },
            alignItems: { base: 'flex-start', tablet: 'center' },
            gap: { base: 8, tablet: 32 },
          })}
        >
          <h2 className={css({ textStyle: 'Title/S', color: 'content.default.level-2' })}>
            <FormattedMessage id='Common.r251104.Announcement' />
          </h2>
          <hr
            className={divider({
              orientation: 'vertical',
              color: 'border.default.level-1',
              h: 32,
              display: { base: 'none', tablet: 'block' },
            })}
          />
          {notice && (
            <m.div
              initial={{ y: 10, opacity: 0.1 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0.1 }}
              transition={{ duration: 0.2 }}
              key={notice.id}
            >
              <IsomorphicLink
                href={notice.html_url}
                newTab
                className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}
              >
                {notice.title}
              </IsomorphicLink>
            </m.div>
          )}
        </div>
        <div
          className={hstack({
            w: { base: '100%', tablet: 'auto' },
            justifyContent: 'flex-end',
            gap: 8,
          })}
        >
          {notices?.map((n, index) => {
            return (
              <button
                type='button'
                value={index}
                className={css(
                  center.raw({
                    textStyle: 'Text/M/Regular',
                    w: 32,
                    h: 32,
                    bgColor: 'background.default.level-3',
                    color: 'content.default.level-1',
                    rounded: 16,
                  }),
                  noticeIndex === index && { color: 'content.default.level-5' },
                )}
                onClick={onClickPagination}
                key={`notice-${n.id}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Announcement;
